import { PageError } from '@/components/page-error';
import { businessApi, locationApi } from '@/lib/api-paths';
import { clearActiveTenant, readActiveTenant, writeActiveTenant } from '@/lib/active-tenant';
import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { ChevronDown, LogOut, Menu, Plus, X } from 'lucide-react';
import { Card } from '@pageboss/ui';
import { PLANS } from '@pageboss/shared';
import { Button } from '@/components/ui/button';
import { LockMarker } from '@/components/lock-marker';
import { Badge } from '@/components/ui/badge';
import { HelpLauncher } from '@/components/help-panel';
import { ErrorBoundary } from '@/components/error-boundary';
import { api } from '@/lib/api';
import { invalidateMe, useMe } from '@/hooks/use-me';
import { useFocusTrap } from '@/lib/use-focus-trap';
import { buildNav, isNavItemActive, type NavChild, type NavGroup } from '@/lib/nav-model';
import { appRoutes, businessRoutes, locationRoutes } from '@/lib/route-map';
import { activeLocationOf, resolveActiveLocation, type LocationItem } from '@/lib/active-location-resolve';
import { switchTarget } from '@/lib/switch-target';
import { isOfferedPair, switchableBusinesses, type SwitchableBusiness } from '@/lib/switcher-model';
import type { DiscoveryResponse } from '@/lib/active-tenant-resolve';
import type { EffectiveTier, MeResolved, MeResponse } from '@/lib/types';

// WP1 Task 14 (glossary sweep): reads the display name straight from
// PLANS[tier].label instead of a hand-duplicated map, so the `pro`-key-
// displays-as-"Starter" trap (locked decision #20) has exactly one source of
// truth — same idiom as billing.tsx's `PLANS[current.plan].label` and
// mailbox-panel.tsx's `tier === 'free' ? 0 : PLANS[tier].freeMailboxesIncluded`.
// 'free' isn't a PLANS key (there is no free tier row), so it stays a literal.
function tierLabel(tier: EffectiveTier): string {
  return tier === 'free' ? 'Free preview' : PLANS[tier].label;
}

interface DomainsListResponse {
  configured: boolean;
  domains: { id: string; status: string }[];
  canFreeDomain: boolean;
}

function userInitials(name: string | null, email: string): string {
  const source = name?.trim() || email;
  return source
    .split(/\s+|@/)
    .filter(Boolean)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');
}

/**
 * `/app/b/:siteId` layout route (WP1 Task 5) — wraps every panel screen in
 * the four-layer shell. Child routes land in Tasks 6-11; this task only
 * ships the shell itself + a placeholder overview stub (see main.tsx).
 */
export function BusinessLayout() {
  return (
    <PanelShell>
      <Outlet />
    </PanelShell>
  );
}

interface PanelShellProps {
  children: ReactNode;
}

export function PanelShell({ children }: PanelShellProps) {
  // T1.6A/B2: the acting tenant is the URL's — proven by the server on every
  // request. `pb.active` only REMEMBERS the pair for the NEXT visit; it never
  // decides which tenant is active and never authorizes a request. The old
  // `useActiveBusiness` hook is gone: it stored a single id and, worse, fell
  // back to the first site in the list.
  const { businessId = '', locationId: paramSiteId } = useTenantParams();
  const meState = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  // BUSINESS-level pages (settings/billing/locations/surveys/mailboxes) name a
  // Business and no Location, so there is no Location here — and the shell must
  // not invent one.
  //
  // It used to fall back to the "last active business" from storage. Under the
  // tenancy model that is wrong twice over: the value is a preference, not
  // authorization, and after the rename it is a BUSINESS id, so putting it in
  // the Location slot builds URLs like /app/b/{b}/l/{b}/… — a Location slot
  // holding a Business id. The Location now comes from the URL or not at all;
  // the sidebar's Location-scoped links simply do not render without one.
  // P2.8: the remembered pair is a HINT, read once and matched below against the
  // accessible list the server returned — it never decides the tenant and is
  // never used while the URL names a Location.
  const [remembered] = useState(() => readActiveTenant());
  // The "location unavailable" notice is ONE-TIME: captured from the redirect's history state on
  // mount, shown on that path only, and never re-shown on reload/back (the history entry is scrubbed).
  const [notice, setNotice] = useState<{ kind: 'location' | 'none'; pathname: string } | null>(null);
  useEffect(() => {
    const kind = (location.state as { locationUnavailable?: 'location' | 'none' } | null)?.locationUnavailable;
    if (kind) {
      // the same shell instance may receive the redirect (Location routes share one BusinessLayout), so the
      // notice is read reactively, then the history entry is scrubbed so reload/back never re-show it
      setNotice({ kind, pathname: location.pathname });
      navigate(location.pathname + location.search, { replace: true, state: null });
      return;
    }
    setNotice((n) => (n && n.pathname !== location.pathname ? null : n));
  }, [location.pathname, location.search, location.state, navigate]);
  const locationUnavailable = notice?.kind ?? null;

  // THE ONE canonical accessible-Location source (P2.8 §3): `/api/businesses`
  // is membership-derived and narrowed on the SERVER to this member's
  // EffectiveScope and to normally navigable rows (never archived). The shell
  // no longer keeps a second, competing list.
  const [discovery, setDiscovery] = useState<DiscoveryResponse | null>(null);
  const [discoveryError, setDiscoveryError] = useState<string | null>(null);
  const [discoveryAttempt, setDiscoveryAttempt] = useState(0);
  const [domainActive, setDomainActive] = useState(false);
  const [unreadInbox, setUnreadInbox] = useState(0);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const switcherPanelRef = useRef<HTMLDivElement>(null);
  const switcherTriggerRef = useRef<HTMLButtonElement>(null);
  const drawerPanelRef = useRef<HTMLDivElement>(null);
  const drawerTriggerRef = useRef<HTMLButtonElement>(null);

  // Business-scoped, not Location-scoped: the switcher spans every Business
  // this member reaches, so it is fetched once and NOT re-keyed on the current
  // tenant. Switching between two Businesses must not re-fetch the list that
  // offers both of them.
  useEffect(() => {
    let cancelled = false;
    setDiscoveryError(null);
    api
      .get<DiscoveryResponse>('/api/businesses')
      .then((res) => {
        if (!cancelled) setDiscovery(res);
      })
      .catch((err: unknown) => {
        // A failed discovery must never be a silent, permanent blank shell: it
        // is shown like a failed /api/me, with a retry (P2.8 review finding).
        if (!cancelled) setDiscoveryError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [discoveryAttempt]);

  // ── P2.8 resolution: the URL is a claim, the server's list is the authority ──
  const switchable = switchableBusinesses(discovery);
  const currentBusiness = switchable.find((b) => b.id === businessId) ?? null;
  const accessible: LocationItem[] = currentBusiness?.locations ?? [];
  const resolution = discovery ? resolveActiveLocation({ businessId, routeLocationId: paramSiteId, saved: remembered, accessible }) : null;
  const activeLocation = resolution ? activeLocationOf(resolution) : null;
  const siteId = activeLocation?.id;

  // Storage self-heal (P2.8 §14): only a pair the server just offered is ever
  // remembered; zero access for this Business drops a stale pair pointing here.
  useEffect(() => {
    if (!resolution || !businessId) return;
    if (resolution.kind === 'route' || resolution.kind === 'fallback' || resolution.kind === 'saved') {
      writeActiveTenant({ businessId, locationId: resolution.location.id });
    } else if (resolution.kind === 'none' && remembered?.businessId === businessId) {
      clearActiveTenant();
    }
  }, [resolution?.kind, resolution && activeLocationOf(resolution)?.id, businessId]);

  useEffect(() => {
    // A Location read needs BOTH halves of the canonical route.
    // Missing either is "no Location in scope": request nothing.
    if (!businessId || !siteId) return;
    let cancelled = false;
    api
      .get<DomainsListResponse>(businessApi(businessId, `domains?locationId=${encodeURIComponent(siteId)}`))
      .then((res) => {
        if (!cancelled) setDomainActive(res.domains.some((d) => d.status === 'active'));
      })
      .catch(() => {});
    api
      .get<{ count: number }>(locationApi(businessId, siteId, 'forms/unread-count'))
      .then((res) => {
        if (!cancelled) setUnreadInbox(res.count);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [businessId, siteId]);

  // Close the mobile drawer whenever the route changes (link tapped).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Business switcher: ESC closes (returns focus to the trigger); a click or
  // tap outside the trigger/panel closes. Mirrors dashboard-shell.tsx:97-122.
  useEffect(() => {
    if (!switcherOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setSwitcherOpen(false);
        switcherTriggerRef.current?.focus();
      }
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (
        !switcherPanelRef.current?.contains(target) &&
        !switcherTriggerRef.current?.contains(target)
      ) {
        setSwitcherOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [switcherOpen]);

  // Mobile drawer: same ESC/click-outside pattern as dashboard-shell.tsx:97-122.
  useEffect(() => {
    if (!drawerOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
        drawerTriggerRef.current?.focus();
      }
    }
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (
        !drawerPanelRef.current?.contains(target) &&
        !drawerTriggerRef.current?.contains(target)
      ) {
        setDrawerOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [drawerOpen]);

  // Keep Tab focus inside the drawer while it's open (@/lib/use-focus-trap),
  // the same primitive dashboard-shell.tsx's dialogs use.
  useFocusTrap(drawerPanelRef, drawerOpen);

  if (meState.kind === 'error') {
    // The shell short-circuits before <Outlet/>, so this is what a customer
    // actually sees when /api/me fails on any nested route — give it the same
    // PageError shape the routes use (h1, eyebrow, outline retry), not a bare
    // red line. Route-level PageError branches stay as the org-level fallback.
    return (
      <div className="px-6 py-8">
        <PageError
          eyebrow="Account"
          title="Your account"
          message={meState.message}
          retry={invalidateMe}
        />
      </div>
    );
  }

  // A BUSINESS-level screen has no Location, and that is a valid state — the
  // shell renders around it with its Location-scoped nav simply unavailable.
  // Blanking here would black out billing, settings and surveys entirely.
  if (meState.kind !== 'ready' || !businessId) {
    return <div className="min-h-screen bg-background" />;
  }

  // INVALID DEEP LINK (P2.8 §13): the URL names a Location the server did not
  // offer for this Business. Nothing of it is rendered or fetched; once the
  // accessible list is known the user is sent to the canonical fallback
  // Location, or — with zero access — to a Business-level screen. The message
  // is generic on purpose: whether the id is archived, foreign or invented is
  // not revealed.
  // A Business the server never offered (foreign, provisional, archived) — on ANY route under it:
  // let /app resolve honestly instead of dressing an unknown tenant in this member's chrome.
  if (discovery && !currentBusiness) return <Navigate to={appRoutes.index()} replace />;
  if (resolution?.kind === 'invalid-route') {
    const to = resolution.fallback ? locationRoutes.overview(businessId, resolution.fallback.id) : businessRoutes.settings(businessId);
    return <Navigate to={to} replace state={{ locationUnavailable: resolution.fallback ? 'location' : 'none' }} />;
  }
  if (discoveryError && !discovery) {
    return (
      <div className="px-6 py-8">
        <PageError eyebrow="Account" title="Your businesses" message={discoveryError} retry={() => setDiscoveryAttempt((n) => n + 1)} />
      </div>
    );
  }
  // The accessible list is not known yet: render nothing Location-scoped rather
  // than guessing (a Location URL must not fetch child data before the list).
  if (!resolution) {
    return <div className="min-h-screen bg-background" />;
  }

  const me = meState.me;
  const shellLocationId = siteId ?? null;

  const nav = buildNav({
    businessId,
    siteId: shellLocationId ?? '',
    tier: me.usage.tier,
    domainActive,
    // No AI-tracking data source is wired into the panel shell in this task
    // (not in the brief's Interfaces list) — an empty list is the honest
    // default until a later task feeds it real tracked engines.
    trackedEngines: [],
    unreadInbox,
    isOperator: me.user.isOperator,
  });

  // Selecting a Location from the switcher. Both ids come from the offer the
  // server made, and the pair is re-checked against that offer before anything
  // is remembered: `pb.active` must never hold a pair this member was not told
  // they may reach. It stays navigation memory either way — the server proves
  // tenancy on every request regardless of what is stored.
  function handleSwitch(targetBusinessId: string, targetLocationId: string) {
    if (!isOfferedPair(switchable, targetBusinessId, targetLocationId)) return;
    writeActiveTenant({ businessId: targetBusinessId, locationId: targetLocationId });
    setSwitcherOpen(false);
    // P2.8 §17: keep the current section when it exists for every Location;
    // a resource-specific page or a non-Location route lands on the overview.
    navigate(switchTarget(location.pathname, targetBusinessId, targetLocationId));
  }

  const sidebarContent = (
    <SidebarBody
      me={me}
      switchable={switchable}
      currentBusinessId={businessId}
      currentBusinessName={currentBusiness?.name ?? ''}
      activeLocation={activeLocation}
      accessibleCount={accessible.length}
      // A business-level screen has no Location; the sidebar renders its
      // Location-scoped links only when there is one.
      siteId={shellLocationId ?? ''}
      nav={nav}
      pathname={location.pathname}
      switcherOpen={switcherOpen}
      onToggleSwitcher={() => setSwitcherOpen((open) => !open)}
      switcherPanelRef={switcherPanelRef}
      switcherTriggerRef={switcherTriggerRef}
      onSwitch={handleSwitch}
    />
  );

  return (
    <div className="min-h-screen bg-background md:grid md:grid-cols-[264px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden border-r bg-card md:flex md:flex-col md:overflow-y-auto">
        {/* Wordmark row. Desktop only: below md the same mark already sits in
            the mobile top bar, and the drawer renders sidebarContent alone —
            so keeping this OUTSIDE SidebarBody avoids a second "pageboss home"
            link in the DOM whenever the drawer is open. `shrink-0` keeps it
            from being squeezed by the scrolling nav below it; the aside itself
            still owns the overflow. `alt=""` because the link's aria-label is
            already its accessible name — an alt would only double it up. */}
        <div className="shrink-0 border-b border-border-subtle px-4 py-4">
          <Link to="/app" aria-label="pageboss home" className="inline-flex items-center">
            <img src="/brand/pageboss-horizontal-primary.svg" alt="" className="h-5 w-auto" />
          </Link>
        </div>
        {sidebarContent}
      </aside>

      <div className="flex min-h-screen flex-col">
        {/* Mobile top bar: hamburger opens the off-canvas drawer below md. */}
        <div className="flex items-center justify-between border-b bg-card px-4 py-3 md:hidden">
          <Link to="/app" className="flex items-center">
            <img
              src="/brand/pageboss-horizontal-primary.svg"
              alt="pageboss"
              className="h-6 w-auto"
            />
          </Link>
          <div className="flex items-center gap-1">
            {/* WP6 Task 10: help launcher next to the hamburger, so help is
                reachable at every width — mounted bare (campaign constraint
                A3, no client analytics on the dashboard). */}
            <HelpLauncher />
            <button
              type="button"
              ref={drawerTriggerRef}
              onClick={() => setDrawerOpen((open) => !open)}
              aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              aria-haspopup="menu"
              aria-expanded={drawerOpen}
              aria-controls="panel-mobile-drawer"
              className="inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-surface-sunken hover:text-foreground"
            >
              {drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {drawerOpen ? (
          <div className="fixed inset-0 z-40 bg-black/40 md:hidden" aria-hidden="true" />
        ) : null}
        {drawerOpen ? (
          <div
            id="panel-mobile-drawer"
            ref={drawerPanelRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col overflow-y-auto border-r bg-card shadow-lg md:hidden"
          >
            {sidebarContent}
          </div>
        ) : null}

        <main className="flex-1 px-6 py-8">
          {locationUnavailable && (
            <p role="status" className="mb-4 rounded-md bg-warning-bg px-3 py-2 text-sm text-warning-fg">
              {locationUnavailable === 'location'
                ? 'That location isn’t available to you. You’ve been taken to one you can open.'
                : 'That location isn’t available to you.'}
            </p>
          )}
          {/* WP8 stage-1 MUST-DO: keyed by pathname so navigating to another
              route (nav/sidebar stay mounted around this boundary, so they
              survive the crash) forces a fresh mount and clears the caught
              error — a stale-chunk lazy-import rejection or any other route
              crash no longer blanks the whole shell. */}
          <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

interface SidebarBodyProps {
  me: MeResolved;
  switchable: SwitchableBusiness[];
  currentBusinessId: string;
  currentBusinessName: string;
  /** the resolved active Location, or null (business-level screen with nothing resolvable, or zero access) */
  activeLocation: LocationItem | null;
  /** how many Locations of the current Business the server offered */
  accessibleCount: number;
  siteId: string;
  nav: ReturnType<typeof buildNav>;
  pathname: string;
  switcherOpen: boolean;
  onToggleSwitcher: () => void;
  switcherPanelRef: RefObject<HTMLDivElement | null>;
  switcherTriggerRef: RefObject<HTMLButtonElement | null>;
  onSwitch: (businessId: string, locationId: string) => void;
}

function SidebarBody({
  me,
  switchable,
  currentBusinessId,
  currentBusinessName,
  activeLocation,
  accessibleCount,
  siteId,
  nav,
  pathname,
  switcherOpen,
  onToggleSwitcher,
  switcherPanelRef,
  switcherTriggerRef,
  onSwitch,
}: SidebarBodyProps) {
  const initials = userInitials(me.user.name, me.user.email);
  const displayName = me.user.name?.trim() || me.user.email;
  const businessName = currentBusinessName || 'Select a business';
  // SidebarBody is mounted TWICE at once (desktop <aside> + mobile drawer,
  // both present in the DOM simultaneously — the desktop copy is merely
  // CSS-hidden below md, not unmounted). useId() gives each mounted instance
  // its own id, so aria-controls/id never collide the way a hardcoded string
  // would once the drawer is open.
  const switcherPanelId = useId();

  return (
    <div className="flex h-full flex-col">
      {/* Business switcher accordion */}
      <div className="border-b border-border-subtle p-3">
        <button
          type="button"
          ref={switcherTriggerRef}
          onClick={onToggleSwitcher}
          aria-haspopup="listbox"
          aria-expanded={switcherOpen}
          aria-controls={switcherPanelId}
          className="flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm font-semibold text-foreground transition-colors hover:bg-surface-sunken"
        >
          <span className="truncate">{businessName}</span>
          <ChevronDown
            aria-hidden="true"
            className={
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform ' +
              (switcherOpen ? 'rotate-180' : '')
            }
          />
        </button>
        {switcherOpen ? (
          <div id={switcherPanelId} ref={switcherPanelRef} className="mt-1">
            <Card className="p-1">
              {/* NESTED: each Business, then the Locations this member may
                  reach inside it. The list is exactly what the server offered
                  — an out-of-scope sibling never arrives in the payload, so it
                  cannot appear here as a row, a count, a link or a disabled
                  item. Provisional and archived Businesses are not offered at
                  all: neither can be acted as. */}
              <div className="flex flex-col gap-1">
                {switchable.map((business) => (
                  <div key={business.id} className="flex flex-col">
                    <p
                      className={
                        'px-2.5 pt-1.5 pb-1 text-xs font-semibold uppercase tracking-wide ' +
                        (business.id === currentBusinessId ? 'text-foreground' : 'text-muted-foreground')
                      }
                    >
                      {business.name}
                    </p>
                    {business.locations.length === 0 ? (
                      // Honest rather than hidden: the member has this
                      // Business but no Location in it they may open.
                      <p className="px-2.5 pb-1 text-sm text-text-tertiary">No accessible locations</p>
                    ) : business.id === currentBusinessId && business.locations.length === 1 ? (
                      // P2.8 §16: exactly one accessible Location in the current
                      // Business — a static label, not a one-item dropdown.
                      <p className="px-2.5 pb-1 text-sm text-foreground" aria-current="location">
                        {business.locations[0]!.name}
                      </p>
                    ) : (
                      <div role="listbox" aria-label={business.name} className="flex flex-col">
                        {business.locations.map((loc) => {
                          const current = business.id === currentBusinessId && loc.id === siteId;
                          return (
                            <button
                              key={loc.id}
                              type="button"
                              role="option"
                              aria-selected={current}
                              onClick={() => onSwitch(business.id, loc.id)}
                              className={
                                'flex min-h-9 items-center rounded-md px-2.5 text-left text-sm transition-colors ' +
                                (current
                                  ? 'bg-accent text-accent-foreground'
                                  : 'text-foreground hover:bg-surface-sunken hover:text-foreground')
                              }
                            >
                              {loc.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to={appRoutes.start({ mode: 'add-business' })}
                  className="mt-1 flex min-h-9 items-center gap-1.5 rounded-md px-2.5 text-sm text-muted-foreground transition-colors hover:bg-surface-sunken hover:text-foreground"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  Add a business
                </Link>
              </div>
            </Card>
          </div>
        ) : null}
      </div>

      {/* Nav groups: Foundation, Visibility, Reputation, Pulse, Reports —
          only around an active Location. Zero access (P2.8 §18) shows a safe
          empty state instead; Business-level screens keep the footer links. */}
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
        {activeLocation ? (
          <>
            <p className="truncate px-2 text-xs text-text-secondary" data-testid="active-location">
              {activeLocation.name}
            </p>
            {nav.groups.map((group) => (
              <NavGroupBlock key={group.key} group={group} pathname={pathname} />
            ))}
          </>
        ) : accessibleCount === 0 ? (
          <p className="px-2 text-sm text-text-secondary" role="status">
            No accessible locations in this business. Ask an owner to add you to one.
          </p>
        ) : null}
      </nav>

      {/* Footer: Locations/Billing/Settings(/Console) + tier badge/avatar/sign-out,
          preserved from dashboard-shell.tsx:160-185. */}
      <div className="border-t border-border-subtle p-3">
        <div className="space-y-0.5">
          {nav.footer.map((item) => (
            <FooterItemRow key={item.label} item={item} pathname={pathname} />
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-border-subtle pt-3">
          <div className="flex min-w-0 items-center gap-2.5">
            {me.user.imageUrl ? (
              <img
                src={me.user.imageUrl}
                alt=""
                className="h-8 w-8 shrink-0 rounded-full border"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">
                {initials}
              </div>
            )}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-foreground">{displayName}</span>
              <Badge variant="muted" className="mt-0.5 w-fit">
                {tierLabel(me.usage.tier)}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* WP6 Task 10: help launcher next to the tier badge/avatar
                block — mounted bare (campaign constraint A3, no client
                analytics on the dashboard). */}
            <HelpLauncher />
            <form method="post" action="/auth/sign-out">
              <Button type="submit" variant="ghost" size="sm" aria-label="Sign out">
                <LogOut className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavGroupBlock({ group, pathname }: { group: NavGroup; pathname: string }) {
  return (
    <div>
      <h2 className="px-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {group.label}
      </h2>
      <div className="mt-1.5 space-y-0.5">
        {group.children.map((child) => (
          <NavChildRow key={child.label} item={child} pathname={pathname} />
        ))}
      </div>
    </div>
  );
}

// Locked NAV CHILD items (Sidebar uses buildNav's `locked` flag verbatim):
// render with a lock marker AND a real link to `item.to` (final-review fix,
// 2026-08-16, finding M2) — the same "locked but clickable" treatment
// FooterItemRow already gives Locations. Locked Mailboxes' `to` points at
// locationRoutes.domainWizard(businessId, siteId) (nav-model.ts — WP3b/D4 swapped this from
// routes.websiteDomain), whose guided wizard explains the domain gate
// honestly; there is no dead end. (Previously this rendered a
// non-interactive `<div>` with a comment deferring click behavior to "Task
// 12" — Task 12 never returned to this file, so the click path was dropped
// between T5 → T10 → T12. Fixed here.)
//
// Locked FOOTER items got this same fix earlier, in Task 10: Locations is
// growth-gated but must stay CLICKABLE — hiding or dead-ending it was the
// old rule (see FooterItemRow below). Its destination (locations.tsx)
// renders the shared `@pageboss/ui` UpgradeGate itself when the tier
// doesn't cover it, so the lock marker here is purely informational, never
// a block. The old top-nav dashboard-shell.tsx (which used to wrap
// billing.tsx, locations.tsx, and settings.tsx) carried the SAME hidden-item
// rule and got the same fix in fix round 1 of Task 10.
//
// T15 fix round 1: settings.tsx/billing.tsx/locations.tsx now render
// PanelShell too (resolved via the `businessId` fallback above, same as the
// 3 survey screens) and dashboard-shell.tsx is deleted — see
// task-15-report.md's "Fix round 1" section.
function NavChildRow({ item, pathname, parentTo }: { item: NavChild; pathname: string; parentTo?: string }) {
  // Both rules — index-child exact matching, and leaf-only highlighting so a
  // parent never wears the pill while one of its children owns the path —
  // live in lib/nav-model.ts alongside the nav shape they describe.
  const isActive = isNavItemActive(item, pathname, parentTo);

  if (item.locked) {
    return (
      // T11/A7: the locked label was `text-muted-foreground/70` — 3.53:1 on
      // the sidebar, an axe color-contrast failure on EVERY desktop route
      // (22 of them), and a banned `/NN` alpha at that. text-text-tertiary is
      // the token that exists for "lightest readable" (5.51:1 on white).
      <Link
        to={item.to}
        className="flex items-center justify-between rounded-md px-2.5 py-2 text-sm text-text-tertiary transition-colors hover:bg-surface-sunken hover:text-foreground"
      >
        <span>{item.label}</span>
        <LockMarker />
      </Link>
    );
  }

  return (
    <div>
      {/* B7: hover used the SAME brand tint as the active row, so passing the
          cursor down the sidebar lit up rows that were not the page you are
          on — the tint stopped meaning "you are here". Every hover in this
          shell is now the neutral sunken surface; `bg-accent` is reserved for
          the active row. */}
      <Link
        to={item.to}
        aria-current={isActive ? 'page' : undefined}
        className={
          'flex items-center justify-between rounded-md px-2.5 py-2 text-sm font-medium transition-colors ' +
          (isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-foreground hover:bg-surface-sunken hover:text-foreground')
        }
      >
        <span>{item.label}</span>
        {item.badge ? (
          <Badge variant="default" className="ml-2">
            {item.badge}
          </Badge>
        ) : null}
      </Link>
      {item.nested ? (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l border-border-subtle pl-3">
          {item.nested.map((nested) => (
            <NavChildRow key={nested.label} item={nested} pathname={pathname} parentTo={item.to} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FooterItemRow({ item, pathname, parentTo }: { item: NavChild; pathname: string; parentTo?: string }) {
  const isActive = isNavItemActive(item, pathname, parentTo);

  if (item.to.startsWith('http')) {
    return (
      <a
        href={item.to}
        className="flex items-center rounded-md px-2.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-surface-sunken hover:text-foreground"
      >
        {item.label}
      </a>
    );
  }

  // Locked footer items (Locations, growth-gated) still navigate — Task 10
  // replaces the old "hide it entirely for non-Growth" rule with "always
  // visible, honest lock marker, real link" (dashboard-shell.tsx's own
  // tier-based splice got the equivalent fix in the same task, fix round 1
  // — see its own comment). The destination page decides what to render
  // for a gated tier.
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      className={
        'flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-sm font-medium transition-colors ' +
        (isActive
          ? 'bg-accent text-accent-foreground'
          : item.locked
            ? 'text-text-tertiary hover:bg-surface-sunken hover:text-foreground'
            : 'text-muted-foreground hover:bg-surface-sunken hover:text-foreground')
      }
    >
      <span>{item.label}</span>
      {item.locked ? <LockMarker /> : null}
    </Link>
  );
}
